import { Helmet } from "react-helmet-async";

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath: string;
  ogImage?: string;
  ogImageAlt?: string;
  type?: "website" | "article" | "event";
  schema?: object | object[];
  noIndex?: boolean;
}


// Base organization schema - used on all pages
const organizationSchema = {
  "@type": "Organization",
  "@id": "https://kultmans.de/#organization",
  "name": "Kultmans e.V.",
  "alternateName": "Kulturmanufaktur im alten Stemke",
  "url": "https://kultmans.de",
  "logo": {
    "@type": "ImageObject",
    "url": "https://kultmans.de/kultmans-logo.png",
    "width": 512,
    "height": 512
  },
  "description": "Kulturverein in Döbeln, Sachsen. Wir bieten Eventlocation mit 500m² Fläche, KI-Workshops, Gesundheitssport, Yoga und fördern Kultur, Bildung, Handwerk und Gemeinschaft im historischen Stemke Werk.",
  "foundingDate": "2023",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Waldheimer Straße 1",
    "addressLocality": "Döbeln",
    "postalCode": "04720",
    "addressRegion": "Sachsen",
    "addressCountry": "DE"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "51.1183442",
    "longitude": "13.1167252"
  },
  "telephone": "+49 3431-5918819",
  "email": "info@kultmans.de",
  "sameAs": [
    "https://www.facebook.com/kultmans",
    "https://www.instagram.com/kultmans"
  ],
  "areaServed": {
    "@type": "GeoCircle",
    "geoMidpoint": {
      "@type": "GeoCoordinates",
      "latitude": "51.1183442",
      "longitude": "13.1167252"
    },
    "geoRadius": "50000"
  },
  "knowsAbout": [
    "Kulturveranstaltungen",
    "Eventlocation",
    "KI-Workshops",
    "Künstliche Intelligenz",
    "Gesundheitssport",
    "Yoga",
    "Gemeinschaftsprojekte"
  ]
};

// Website schema
const websiteSchema = {
  "@type": "WebSite",
  "@id": "https://kultmans.de/#website",
  "url": "https://kultmans.de",
  "name": "Kultmans e.V. | Kulturmanufaktur im alten Stemke",
  "description": "Kulturverein in Döbeln, Sachsen für Kultur, Bildung, Handwerk und Gemeinschaft",
  "publisher": { "@id": "https://kultmans.de/#organization" },
  "inLanguage": "de-DE"
};

// Local Business schema for better local SEO
const localBusinessSchema = {
  "@type": "LocalBusiness",
  "@id": "https://kultmans.de/#localbusiness",
  "name": "Kultmans e.V.",
  "description": "Kulturverein und Eventlocation im historischen Stemke Werk in Döbeln, Sachsen",
  "url": "https://kultmans.de",
  "telephone": "+49 3431-5918819",
  "email": "info@kultmans.de",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Waldheimer Straße 1",
    "addressLocality": "Döbeln",
    "postalCode": "04720",
    "addressRegion": "Sachsen",
    "addressCountry": "DE"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "51.1183442",
    "longitude": "13.1167252"
  },
  "openingHoursSpecification": {
    "@type": "OpeningHoursSpecification",
    "description": "Nach Vereinbarung und während Veranstaltungen"
  },
  "priceRange": "€-€€"
};

export function SEO({
  title,
  description,
  keywords,
  canonicalPath,
  ogImage = "https://kultmans.de/images/hero-stemke-werk.webp",
  ogImageAlt,
  type = "website",

  schema,
  noIndex = false,
}: SEOProps) {
  // Force noindex on any non-custom-domain (lovable.app, localhost, etc.)
  const isCustomDomain = typeof window !== "undefined" &&
    (window.location.hostname === "kultmans.de" || window.location.hostname === "www.kultmans.de");
  const effectiveNoIndex = noIndex || !isCustomDomain;
  const canonicalUrl = `https://kultmans.de${canonicalPath}`;
  const fullTitle = canonicalPath === "/" 
    ? title 
    : `${title} | Kultmans e.V.`;

  // Build the full schema graph
  const schemaGraph = {
    "@context": "https://schema.org",
    "@graph": [
      organizationSchema,
      websiteSchema,
      localBusinessSchema,
      {
        "@type": "WebPage",
        "@id": canonicalUrl,
        "url": canonicalUrl,
        "name": fullTitle,
        "description": description,
        "isPartOf": { "@id": "https://kultmans.de/#website" },
        "about": { "@id": "https://kultmans.de/#organization" },
        "inLanguage": "de-DE",
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Start",
              "item": "https://kultmans.de"
            },
            ...(canonicalPath !== "/" ? [{
              "@type": "ListItem",
              "position": 2,
              "name": title.split(" | ")[0],
              "item": canonicalUrl
            }] : [])
          ]
        }
      },
      ...(Array.isArray(schema) ? schema : schema ? [schema] : [])
    ]
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />

      {/* Robots */}
      <meta name="robots" content={effectiveNoIndex ? "noindex, nofollow" : "index, follow"} />
      <meta name="googlebot" content={effectiveNoIndex ? "noindex, nofollow" : "index, follow"} />

      {/* Author & Language */}
      <meta name="author" content="Kultmans e.V." />
      <meta httpEquiv="content-language" content="de" />

      {/* Geo Tags for Local SEO */}
      <meta name="geo.region" content="DE-SN" />
      <meta name="geo.placename" content="Döbeln" />
      <meta name="geo.position" content="51.1183442;13.1167252" />
      <meta name="ICBM" content="51.1183442, 13.1167252" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:secure_url" content={ogImage} />
      <meta property="og:image:alt" content={ogImageAlt ?? fullTitle} />
      <meta property="og:locale" content="de_DE" />
      <meta property="og:site_name" content="Kultmans e.V." />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={ogImageAlt ?? fullTitle} />


      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(schemaGraph)}
      </script>
    </Helmet>
  );
}

export default SEO;
